import React, { useState, useEffect, useCallback } from "react";
import { collection, query, where, getCountFromServer } from "firebase/firestore";
import { db } from "../firebase";
import { useAdminAuth } from "./AdminAuth";

const menuItems = [
  { icon: "fa-house", label: "Genel Bakış", id: "dashboard" },
  { icon: "fa-users", label: "Üye Listesi", id: "members" },
  { icon: "fa-file-lines", label: "Üyelik Talepleri", id: "applications" },
  { icon: "fa-ticket", label: "Avantaj Başvuruları", id: "avantajBasvurulari" },
  { icon: "fa-graduation-cap", label: "Eğitim Başvuruları", id: "egitimBasvurulari" },
  { icon: "fa-calendar-check", label: "Etkinlik Kayıtları", id: "etkinlikKayitlari" },
  { icon: "fa-envelope", label: "İletişim Mesajları", id: "iletisimMesajlari" },
  { icon: "fa-handshake", label: "Anlaşmalı Kurumlar", id: "kurumlar" },
  { icon: "fa-calendar-day", label: "Etkinlikler", id: "etkinlikler" },
  { icon: "fa-bullhorn", label: "Duyurular", id: "duyurular" },
  { icon: "fa-comment-sms", label: "SMS Gönder", id: "sms" },
  { icon: "fa-rectangle-list", label: "SMS Şablonları", id: "templates" },
  { icon: "fa-cake-candles", label: "Doğum Günleri", id: "birthdays" },
  { icon: "fa-clock-rotate-left", label: "SMS Geçmişi", id: "sms-log" },
];

// Bekleyen başvuru rozeti gösterilecek sekmeler: koleksiyon + "bekleyen" filtresi.
// Bir kayıt admin tarafından işlenip durumu değiştiğinde sayı otomatik düşer.
const BADGE_CONFIG: Record<string, { collection: string; field: string; value: string }> = {
  applications: { collection: "uyelikTalepleri", field: "durum", value: "bekliyor" },
  avantajBasvurulari: { collection: "avantajBasvurulari", field: "durum", value: "bekliyor" },
  egitimBasvurulari: { collection: "egitimBasvurulari", field: "durum", value: "bekliyor" },
  etkinlikKayitlari: { collection: "etkinlikKayitlari", field: "durum", value: "bekliyor" },
  iletisimMesajlari: { collection: "iletisimMesajlari", field: "durum", value: "yeni" },
};

interface Props {
  activeTab: string;
  onTab: (id: string) => void;
  children: React.ReactNode;
}

const AdminLayout: React.FC<Props> = ({ activeTab, onTab, children }) => {
  const { logout } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badges, setBadges] = useState<Record<string, number>>({});

  const loadBadges = useCallback(async () => {
    try {
      const entries = Object.entries(BADGE_CONFIG);
      const counts = await Promise.all(
        entries.map(([, cfg]) =>
          getCountFromServer(query(collection(db, cfg.collection), where(cfg.field, "==", cfg.value)))
        )
      );
      const next: Record<string, number> = {};
      entries.forEach(([id], i) => { next[id] = counts[i].data().count; });
      setBadges(next);
    } catch (e) {
      console.error("Bekleyen sayıları okunamadı:", e);
    }
  }, []);

  // İlk yüklemede + sekme değişiminde tazele (bir sekmede işlem yapılıp başka
  // sekmeye geçildiğinde sayı otomatik günceller — ayrı bir "görüldü" mekanizması gerekmez).
  useEffect(() => { loadBadges(); }, [activeTab, loadBadges]);

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Sidebar overlay (mobile) */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 z-40 flex flex-col transition-transform duration-300
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 lg:static lg:flex`}
        style={{ background: "linear-gradient(180deg, #0F172A 0%, #1A2540 100%)" }}>

        {/* Logo */}
        <div className="px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center font-extrabold text-white text-sm"
              style={{ background: "linear-gradient(135deg, #8B1428, #0F4C81)" }}>61</div>
            <div>
              <div className="text-white font-bold text-sm leading-tight">Admin Paneli</div>
              <div className="text-white/50 text-xs">Trabzonlular Derneği</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {menuItems.map(item => {
            const bekleyen = badges[item.id] ?? 0;
            return (
              <button key={item.id} onClick={() => { onTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors
                  ${activeTab === item.id
                    ? "bg-white/10 text-white border-r-2 border-[#8B1428]"
                    : "text-white/60 hover:text-white hover:bg-white/5"}`}>
                <i className={`fa-solid ${item.icon} w-5 text-center`} />
                <span className="flex-1 text-left">{item.label}</span>
                {bekleyen > 0 && (
                  <span className="bg-red-600 text-white text-[11px] font-bold min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center">
                    {bekleyen > 99 ? "99+" : bekleyen}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-white/10">
          <button onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 text-sm transition-colors">
            <i className="fa-solid fa-right-from-bracket" />
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4 sticky top-0 z-20">
          <button className="lg:hidden text-gray-500" onClick={() => setSidebarOpen(true)}>
            <i className="fa-solid fa-bars text-xl" />
          </button>
          <div className="flex-1">
            <span className="font-bold text-gray-800">
              {menuItems.find(m => m.id === activeTab)?.label ?? "Admin"}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <i className="fa-solid fa-circle-user text-lg text-gray-400" />
            <span className="hidden sm:inline">info@bursatrabzonlulardernegi.org</span>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;